import { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuditLog } from "@/hooks/useAuditLog";
import { useAuditLogExport } from "@/hooks/useAuditLogExport";
import { AuditLogEntry } from "@/services/auditLogApi";
import { diffFields, formatValue } from "@/lib/auditDiff";
import {
  ACTION_LABELS,
  ACTION_OPTIONS,
  AUDIT_ENTRY_LIMIT,
  CHANGE_TYPE_ACTIONS,
  CHANGE_TYPE_OPTIONS,
  STAGE_BADGE_VARIANT,
  type AuditChangeType,
} from "@/constants/audit";
import { ALL_FILTER_VALUE, AuditFilterSelect } from "@/components/audit/AuditFilterSelect";
import { AuditDiffValue } from "@/components/audit/AuditDiffValue";

/** A workflow step: `key` is what entries are stored and filtered by, `label` what users see. */
export interface AuditStageOption {
  key: string;
  label: string;
}

interface AuditHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  stages: AuditStageOption[];
  defaultStage?: string;
}

const ALL = ALL_FILTER_VALUE;

const ACTION_FILTER_OPTIONS = ACTION_OPTIONS.map((action) => ({
  value: action,
  label: ACTION_LABELS[action],
}));

const CHANGE_TYPE_FILTER_OPTIONS = CHANGE_TYPE_OPTIONS.map((changeType) => ({
  value: changeType,
  label: changeType,
}));

const AuditHistoryDialog = ({ open, onClose, projectId, stages, defaultStage }: AuditHistoryDialogProps) => {
  const [stage, setStage] = useState<string>(defaultStage ?? ALL);
  const [action, setAction] = useState<string>(ALL);
  const [changeType, setChangeType] = useState<string>(ALL);
  const [user, setUser] = useState<string>(ALL);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // The dialog stays mounted (just hidden) between opens, so re-sync the filters to
  // whichever step it was opened from every time it opens, instead of only on first mount.
  useEffect(() => {
    if (open) {
      setStage(defaultStage ?? ALL);
      setAction(ALL);
      setChangeType(ALL);
      setUser(ALL);
      setExpandedId(null);
    }
  }, [open, defaultStage]);

  const { data: entries = [], isLoading } = useAuditLog(
    open ? projectId : undefined,
    { stage: stage === ALL ? undefined : stage, limit: AUDIT_ENTRY_LIMIT }
  );
  const stageLabels = useMemo(
    () => Object.fromEntries(stages.map((s) => [s.key, s.label])),
    [stages]
  );
  const { exportToCsv, exportToXlsx } = useAuditLogExport(stageLabels);

  const stageOptions = useMemo(() => stages.map((s) => ({ value: s.key, label: s.label })), [stages]);

  const userOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const entry of entries) {
      if (entry.user && entry.user_name) seen.set(entry.user, entry.user_name);
    }
    return Array.from(seen, ([value, label]) => ({ value, label }));
  }, [entries]);

  // Change type is a client-side grouping of actions, applied over the fetched entries
  // exactly as the action and user filters are. Stage stays the server-side filter, so
  // only one stage's trail is ever fetched.
  const filteredEntries = useMemo(() => {
    const changeTypeActions =
      changeType === ALL ? null : new Set<string>(CHANGE_TYPE_ACTIONS[changeType as AuditChangeType]);
    return entries.filter(
      (e) =>
        (action === ALL || e.action === action) &&
        (user === ALL || e.user === user) &&
        (changeTypeActions === null || changeTypeActions.has(e.action))
    );
  }, [entries, action, user, changeType]);

  const toggleExpand = (id: string) => setExpandedId((prev) => (prev === id ? null : id));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-[1320px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Audit History</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 flex-wrap">
          <AuditFilterSelect
            placeholder="Stage"
            allLabel="All Stages"
            value={stage}
            options={stageOptions}
            onChange={setStage}
          />
          <AuditFilterSelect
            placeholder="Change Type"
            allLabel="All Change Types"
            value={changeType}
            options={CHANGE_TYPE_FILTER_OPTIONS}
            onChange={setChangeType}
          />
          <AuditFilterSelect
            placeholder="Action"
            allLabel="All Actions"
            value={action}
            options={ACTION_FILTER_OPTIONS}
            onChange={setAction}
          />
          <AuditFilterSelect
            placeholder="User"
            allLabel="All Users"
            value={user}
            options={userOptions}
            onChange={setUser}
          />

          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant="outline"
              size="sm"
              disabled={!filteredEntries.length}
              onClick={() => exportToCsv(filteredEntries, `${projectId}-audit-log.csv`)}
            >
              <Download className="w-4 h-4" /> Export CSV
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!filteredEntries.length}
              onClick={() => exportToXlsx(filteredEntries, `${projectId}-audit-log.xlsx`)}
            >
              <Download className="w-4 h-4" /> Export Excel
            </Button>
          </div>
        </div>

        {entries.length >= AUDIT_ENTRY_LIMIT && (
          <p className="text-xs text-muted-foreground">
            Showing the {AUDIT_ENTRY_LIMIT} most recent entries for this stage. Narrow the stage
            filter to see older ones.
          </p>
        )}

        <div className="flex-1 overflow-auto border rounded-md">
          <AuditEntryTable
            entries={filteredEntries}
            stageLabels={stageLabels}
            isLoading={isLoading}
            expandedId={expandedId}
            onToggle={toggleExpand}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AuditEntryTable = ({
  entries,
  stageLabels,
  isLoading,
  expandedId,
  onToggle,
}: {
  entries: AuditLogEntry[];
  stageLabels: Record<string, string>;
  isLoading: boolean;
  expandedId: string | null;
  onToggle: (id: string) => void;
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading audit history...
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-muted-foreground">
        No audit entries match the current filters.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-8" />
          <TableHead>Timestamp</TableHead>
          <TableHead>User</TableHead>
          <TableHead>Action</TableHead>
          <TableHead>Stage</TableHead>
          <TableHead>Item</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {entries.map((entry) => (
          <AuditHistoryRow
            key={entry.id}
            entry={entry}
            stageLabels={stageLabels}
            isExpanded={expandedId === entry.id}
            onToggle={() => onToggle(entry.id)}
          />
        ))}
      </TableBody>
    </Table>
  );
};

const AuditHistoryRow = ({
  entry,
  stageLabels,
  isExpanded,
  onToggle,
}: {
  entry: AuditLogEntry;
  stageLabels: Record<string, string>;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const fields = useMemo(() => diffFields(entry.before, entry.after), [entry.before, entry.after]);
  const Chevron = isExpanded ? ChevronDown : ChevronRight;

  return (
    <>
      <TableRow className="cursor-pointer" onClick={onToggle}>
        <TableCell>
          <Chevron className="w-4 h-4" />
        </TableCell>
        <TableCell className="whitespace-nowrap">{new Date(entry.created_at).toLocaleString()}</TableCell>
        <TableCell>{entry.user_name ?? "—"}</TableCell>
        <TableCell>
          <Badge variant="secondary">{ACTION_LABELS[entry.action] ?? entry.action}</Badge>
        </TableCell>
        <TableCell>
          {entry.stage && (
            <Badge variant={STAGE_BADGE_VARIANT[entry.stage] ?? "outline"}>
              {stageLabels[entry.stage] ?? entry.stage}
            </Badge>
          )}
        </TableCell>
        <TableCell>{entry.entity_name ?? entry.entity_id}</TableCell>
      </TableRow>
      {isExpanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30">
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">Before</div>
                <FieldList fields={fields} side="before" />
              </div>
              <div>
                <div className="text-xs font-semibold text-muted-foreground mb-1">After</div>
                <FieldList fields={fields} side="after" />
              </div>
            </div>
            {entry.comment && (
              <div className="mt-2 pt-2 border-t text-sm">
                <span className="font-semibold text-muted-foreground">Comment: </span>
                {entry.comment}
              </div>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/** Above this, a string value is prose rather than a field value and gets the line diff. */
const LONG_TEXT_LENGTH = 200;

const isLongText = (value: unknown): value is string =>
  typeof value === "string" && (value.length > LONG_TEXT_LENGTH || value.includes("\n"));

const asText = (value: unknown): string => (typeof value === "string" ? value : "");

const FieldRow = ({
  field,
  side,
}: {
  field: ReturnType<typeof diffFields>[number];
  side: "before" | "after";
}) => {
  if (isLongText(field.before) || isLongText(field.after)) {
    return (
      <div>
        <dt className="font-medium mb-0.5">{field.key}:</dt>
        <dd>
          <AuditDiffValue before={asText(field.before)} after={asText(field.after)} side={side} />
        </dd>
      </div>
    );
  }

  return (
    <div className={field.changed ? "bg-yellow-100/60 rounded px-1 -mx-1" : undefined}>
      <dt className="inline font-medium">{field.key}: </dt>
      <dd className="inline">{formatValue(side === "before" ? field.before : field.after)}</dd>
    </div>
  );
};

const FieldList = ({
  fields,
  side,
}: {
  fields: ReturnType<typeof diffFields>;
  side: "before" | "after";
}) => {
  if (fields.length === 0) {
    return <div className="text-sm text-muted-foreground">—</div>;
  }
  return (
    <dl className="space-y-1 text-sm">
      {fields.map((field) => (
        <FieldRow key={field.key} field={field} side={side} />
      ))}
    </dl>
  );
};

export default AuditHistoryDialog;
